import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import Annotations from '../../components/annotations/annotations';
import Loading from '../../components/loading/loading';
import TabHeader, { TabHeaderItem } from '../../components/tabs/tab-header';
import { Note, NoteType } from '../../model/note';
import { IRootState } from '../../reducer';
import { openPaidDiseaseModal } from '../../reducer/globalModal/actions';
import MedicalCalculatorHeader from './medical-calculator-header';
import MedicalCalculatorList from './medical-calculator-list';
import MedicalCalculatorMarkdown from './medical-calculator-markdown';
import MedicalCalculatorParams from './medical-calculator-params/medical-calculator-params';
import MedicalCalculatorResult from './medical-calculator-result';
import { useMedicalCalculatorContext } from './medical-calculator.provider';
import './medical-calculator.scss';

interface Props extends RouteComponentProps<{}, {}, { fromAnnotation?: boolean }> {
    t: any;
    hasSubscription?: boolean;
    openPaidModal: () => void;
}

const MedicalCalculatorDashboard = (props: Props) => {
    const [tabItems] = useState<TabHeaderItem[]>([
        { index: 0, title: props.t('medicalCalculator.tabs.calculator') },
        { index: 1, title: props.t('medicalCalculator.tabs.interpretation') },
        { index: 2, title: props.t('medicalCalculator.tabs.evidence') }
    ]);
    const [showContent, setShowContent] = useState<boolean>(false);
    const [isLoadingScreen, setIsLoadingScreen] = useState<boolean>(true);
    const {
        medicalCalculator,
        showAnnotation,
        medicalCalculatorList,
        isLoading,
        setNewCurrentMedicalCalculator,
        setShowAnnotation,
        setMedicalCalculator
    } = useMedicalCalculatorContext();

    useEffect(() => {
        if (medicalCalculator !== undefined) {
            if (props.location?.state?.fromAnnotation != null) {
                const fromAnnotation = props.location?.state?.fromAnnotation ?? false;
                setShowAnnotation(fromAnnotation);
            }
            setShowContent(true);
            setIsLoadingScreen(false);
        }
    }, [medicalCalculator]);

    useEffect(() => {
        if (medicalCalculatorList.length > 0) {
            const {
                match: { params }
            } = props;
            const medicalCalculatorId: number = (params as any).medicalCalculatorId ?? 28;
            setNewCurrentMedicalCalculator({ id: medicalCalculatorId });
        }
    }, [medicalCalculatorList]);

    const onSaveNote = (note: Note) => {
        setMedicalCalculator({ ...medicalCalculator, note });
    };

    return isLoadingScreen ? (
        <div className="disease-screen-container">
            <Loading />
        </div>
    ) : (
        <div className={'dose-main-container'}>
            <div className={'screen-dose-search-container'}>
                <MedicalCalculatorList {...props} />
            </div>
            <div className={'screen-dose-content-container'} style={{ display: showContent ? 'block' : 'none' }}>
                <div style={{ display: isLoading ? 'flex' : 'none', height: '100%', alignItems: 'center', justifyItems: 'center' }}>
                    <Loading />
                </div>
                <div style={{ display: isLoading ? 'none' : 'block', height: '100%' }}>
                    <MedicalCalculatorHeader {...props} />
                    <TabHeader tabs={tabItems}>
                        <div>
                            <MedicalCalculatorParams />
                            <MedicalCalculatorResult />
                        </div>
                        <MedicalCalculatorMarkdown
                            {...props}
                            hasSubscription={props.hasSubscription}
                            markdownContent={medicalCalculator?.interpretationText}
                        />
                        <MedicalCalculatorMarkdown
                            {...props}
                            hasSubscription={props.hasSubscription}
                            markdownContent={medicalCalculator?.evidenceText}
                        />
                    </TabHeader>
                </div>
            </div>
            <div>
                {showAnnotation && (
                    <Annotations
                        note={medicalCalculator?.note}
                        itemId={medicalCalculator?.id!}
                        noteType={NoteType.MEDICAL_CALCULATOR}
                        saveCallback={onSaveNote}
                    />
                )}
            </div>
        </div>
    );
};

const mapStateToProps = ({ authentication }: IRootState) => ({
    hasSubscription: authentication.account?.customer?.hasSubscription === true
});

const mapDispatchToProps = {
    openPaidModal: openPaidDiseaseModal
};

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(MedicalCalculatorDashboard) as React.ComponentType<
    any
>;
